import type { FollowUpItemState } from './types';

/**
 * Every word the follow-up screens say, and the pure functions that build the rest.
 *
 * Copy and logic live here rather than inline in the components for the reason
 * `attendee-preview/constants.ts` gives: **there is no DOM renderer in this repository**, so a
 * sentence inside a `.tsx` can only be asserted by scanning source, while a sentence returned by a
 * function can be asserted directly. Anything an acceptance criterion quotes belongs in this file.
 *
 * This module imports nothing but its own types — no `@/` alias, no React, no API client — so the
 * test runner can load it without a bundler.
 *
 * ## Where this copy comes from
 *
 * The authoritative prototype is `H2H_ Wireframe-handoff (1).zip` → `Member App.dc.html`, screens
 * `FOLLOWUP` (index), `FOLLOWUP` (detail) and the follow-up composer overlay. Strings taken from
 * it are marked *prototype*. Two categories are **not** in the prototype and are written here:
 *
 *   1. **Everything about copying.** The prototype's composer ends in `Send message` and is backed
 *      by a Messages inbox and a threaded conversation view. ADR 032 put all three in Phase 2, so
 *      `MEM-038` AC-1 replaced the primary action with **Copy opener**, and there is no Copy
 *      affordance anywhere in the prototype to match against — that copy is new and the deviation
 *      was recorded in the PR. **ADR 052 brought the other three back** (`MEM-054`, `MEM-055`), so
 *      Send is the primary action again ({@link SEND_OPENER_LABEL}) and Copy is the secondary one
 *      beside it. Copy is kept rather than removed: `MEM-038` AC-6's connection with no channel is
 *      now joined by `MEM-054` AC-5's disconnected pair, and in both cases the draft is still the
 *      member's to take somewhere else.
 *   2. **Every empty state.** The prototype has none on either follow-up screen — an event with no
 *      connections renders a header, a hardcoded `2`/`2` stats card and then nothing. `MEM-035`
 *      AC-4/AC-5 make that exact case the one that matters most, so it is written rather than
 *      copied.
 */

/**
 * **One event's follow-up summary** (`MEM-036` AC-2).
 *
 * A function rather than a template literal at each call site, for the reason `orderDetailPath` and
 * `ticketPath` are: two spellings of one route is how a link comes to point at a screen that no
 * longer exists there. Two callers today — the index's own rows and the past-event panel on the
 * event page, which is where a member goes looking for what an evening produced.
 */
export function followUpEventPath(eventId: string): string {
  return `/app/follow-up/events/${encodeURIComponent(eventId)}`;
}

// ─── the index (`MEM-036` AC-1) ───────────────────────────────────────────────────────────────

/**
 * *prototype* — the word the detail screen is headed with and its back link reads. It headed the
 * index too, until the index became Your Events' *My follow up* column (2026-09-11); the detail
 * screen's own header is `MEM-053`'s, in the per-event phase.
 */
export const FOLLOW_UP_INDEX_TITLE = 'Follow up';

/**
 * The empty follow-up list. **Not in the prototype** — written here, for the index, and now
 * Your Events' *My follow up* column when it has nothing in it.
 *
 * A member reaches this before their first event, so it is an anticipation rather than a
 * disappointment, and it says what will fill the list rather than that the list is empty.
 */
export const FOLLOW_UP_INDEX_EMPTY_DESCRIPTION =
  'After an event you have been to, the people you connected with turn up here with a draft ' +
  'message ready to go.';

/** *prototype* — `1 follow-up ready` / `2 follow-ups ready`. */
export function readyChipLabel(count: number): string | null {
  if (count <= 0) return null;
  return count === 1 ? '1 follow-up ready' : `${String(count)} follow-ups ready`;
}

/**
 * *prototype* — the desktop handoff's third chip, for an event whose list is clear.
 *
 * Shown only when there were connections to clear. An event a member logged nothing at gets
 * {@link REDUCED_INDEX_CHIP} instead: "All followed up" on a list that never had anybody on it
 * would be a small lie, and a congratulation for having done nothing is exactly the tone
 * `MEM-035` AC-5 is about.
 */
export const ALL_FOLLOWED_UP_CHIP = 'All followed up';

/** For a reduced entry on the index. Neutral, and not a count of zero. */
export const REDUCED_INDEX_CHIP = 'Recap only';

export function indexChipFor(entry: {
  readonly connectionCount: number;
  readonly followUpCount: number;
}): string {
  if (entry.connectionCount === 0) return REDUCED_INDEX_CHIP;
  return readyChipLabel(entry.followUpCount) ?? ALL_FOLLOWED_UP_CHIP;
}

// ─── the detail (`MEM-036` AC-2, AC-3, AC-4) ──────────────────────────────────────────────────

/** *prototype* — AC-3's two counts, side by side. */
export const CONNECTIONS_COUNT_LABEL = 'Connections';
export const FOLLOW_UP_COUNT_LABEL = 'To follow up';

/** *prototype* — the section over the list. */
export const PEOPLE_SECTION_TITLE = 'People you met';
export const PEOPLE_SECTION_SUBTITLE = 'Tap Follow up for a message you can send in seconds.';

/**
 * **The reduced summary, on the screen** (`MEM-035` AC-4, AC-5).
 *
 * The email is the other half of this and its copy is chosen in `message-templates/types.ts`; the
 * two say the same thing because a member who reads one and then opens the other should not find
 * the platform changing its story. The same three rules apply:
 *
 *   - the recap first, plainly, and the member acknowledged as having been there;
 *   - the empty list framed as **less to do** rather than as something missing;
 *   - exactly one line of reassurance, and **no advice**. No "try connecting with more people
 *     next time". `MEM-035` AC-5 cites `MEM-MAT-014` because the matching empty state had the
 *     same trap: a tip in an empty state reads as a diagnosis of the person reading it.
 */
export const REDUCED_SUMMARY_TITLE = 'Nothing to follow up on from this one';
export const REDUCED_SUMMARY_DESCRIPTION =
  'Nothing was logged at this event, so there is nothing waiting for you to do. That happens ' +
  'more often than you would think, and it says nothing about how your evening went.';

/** The recap line under the reduced state: the event, its date, its venue. */
export function eventRecapLine(event: {
  readonly name: string;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly timezone: string | null;
  readonly venueName: string | null;
}): string {
  const when = formatEventDate(event.endsAt ?? event.startsAt, event.timezone);
  return [event.name, when, event.venueName].filter((part) => part !== null).join(' · ');
}

/**
 * "23 September 2026", in **the event's own** timezone.
 *
 * Explicit `timeZone`, never the browser's, per NFR time-and-timezone#display: a member in London
 * reading about an evening in Providence must see Providence's date, and the API, the email and
 * this screen all have to say the same one. `null` for an event with no instant — a real case for
 * an event whose organizer never set one.
 */
export function formatEventDate(instant: string | null, timezone: string | null): string | null {
  if (instant === null) return null;
  const parsed = Date.parse(instant);
  if (Number.isNaN(parsed)) return null;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(timezone === null ? {} : { timeZone: timezone }),
  }).format(new Date(parsed));
}

// ─── item states (`MEM-036` AC-4, `MEM-038` AC-3, AC-4, AC-5) ─────────────────────────────────

/**
 * How the list tells the three states apart (`MEM-036` AC-4).
 *
 * The prototype models **two** — `Follow up` and `Followed up` — because it has no concept of
 * clearing an item without sending. `MEM-038` AC-4 adds `done` as a distinct, separately reachable
 * action, and AC-5 requires `sent` and `done` to stay distinguishable, so the third label is
 * written here. `Handled` rather than a second `Followed up`: the member asserted they dealt with
 * it, and saying they followed up would put words in their mouth that AC-4 explicitly separates.
 */
export const ITEM_STATE_LABELS: Readonly<Record<FollowUpItemState, string>> = {
  ready: 'Follow up',
  sent: 'Followed up',
  done: 'Handled',
};

/** `ready` is the only state with an action left on it. */
export function itemIsOutstanding(state: FollowUpItemState): boolean {
  return state === 'ready';
}

export function itemStateLabel(state: FollowUpItemState): string {
  return ITEM_STATE_LABELS[state];
}

// ─── the composer (`MEM-037` AC-2, AC-3, `MEM-038` AC-1, AC-2, AC-6) ──────────────────────────

/** *prototype*, and `MEM-037` AC-2 verbatim: `Follow up with {name}`. */
export function composerTitle(person: { readonly firstName: string }): string {
  return `Follow up with ${person.firstName}`;
}

/** *prototype* — the picker's section label. */
export const OPENER_PICKER_LABEL = 'Pick a message to start from';

/** *prototype* — the fourth, dashed option, and the editable draft's label. */
export const START_FROM_SCRATCH_LABEL = 'Start from scratch';
export const DRAFT_LABEL = 'Your message';

/**
 * **The secondary action, since ADR 052** (`MEM-038` AC-1).
 *
 * Copy was the *primary* action for as long as ADR 032 stood: the prototype says `Send message`,
 * there was no message surface, and `MEM-038` AC-1 made Copy the button. That was the single most
 * visible deviation from the handoff on this surface. ADR 052 supersedes ADR 032 and `MEM-054`
 * AC-4 builds Send, so the deviation is over and {@link SEND_OPENER_LABEL} leads.
 *
 * Copy stays beside it, and is not a leftover: `MEM-038` AC-6 (a connection who published no
 * channel) and `MEM-054` AC-5 (a pair who disconnected) are both cases where the platform cannot
 * deliver the draft and the member still wants it.
 */
// ─── the follow-up page (`Web App.dc.html` §2.7) ────────────────────────────────────────────────

/**
 * The page's heading and the sentence under it — §2.7, item 3, verbatim.
 *
 * The prototype draws these as a **second `<h1>`** under the event header card's. Ours is an `<h2>`:
 * the card's name is the page's subject and its `<h1>`, and two top-level headings on one screen is
 * the prototype's own slip rather than a decision (`handoff-corrections.md`).
 *
 * *"Edit and send"* was kept while the primary action was Copy, on the argument that the member
 * edits and then sends it somewhere — LinkedIn, email, wherever the person published a channel —
 * so the sentence stayed true and would stay true when Send landed. Send has landed
 * (`MEM-054` AC-4) and it does, literally.
 */
export const OPENERS_HEADING = 'Ready-to-send openers';
export const OPENERS_SUBHEAD =
  "Personalized from your conversations. Edit and send while they're fresh.";

/** §2.7, item 4's empty card, verbatim. */
export const NO_FOLLOW_UPS_TITLE = 'No follow-ups for this event';
export const NO_FOLLOW_UPS_DESCRIPTION =
  "Openers appear here once you've logged the people you met at this event.";

/**
 * The card's way into the composer (`MEM-037` AC-2, AC-3).
 *
 * The 10 September card has no such control: its draft is inline and there is no picker anywhere in
 * the file. `MEM-037` AC-3 requires *"a picker over the generated opener set"*, and putting three
 * alternative paragraphs inside a card in a two-column grid would bury the draft. So the card keeps
 * the inline draft the prototype draws, and the openers live one tap away in the overlay AC-2
 * already specifies.
 */
export const OTHER_OPENERS_LABEL = 'Other openers';

/** What a member reads when the clipboard refuses — an insecure context, or a denied permission. */
export const COPY_FAILED =
  'We could not reach your clipboard. The message is in the box above — select it and copy it by hand.';

export const COPY_OPENER_LABEL = 'Copy opener';
export const COPIED_LABEL = 'Copied';

/** The secondary action (`MEM-038` AC-4), worded as the AC words it. */
export const MARK_DONE_LABEL = 'Done';
export const MARK_DONE_DESCRIPTION = "I've handled this";

/** What copying tells the member happened, since the platform did not send anything. */
export const COPIED_NOTICE =
  'Copied. Paste it wherever you two are in touch — this is marked as followed up.';

/**
 * Where to send it (`MEM-038` AC-2), and what to say when there is nowhere (AC-6).
 *
 * AC-6 is a connection who published no channel — somebody the platform cannot route the member
 * to *off* H2HSpark. ADR 032 made that the common case rather than the rare one, because a
 * channel was the only route there was; under ADR 052 there is a thread, so this notice is about
 * the outside world only, and it appears beside a Send that works. The screen says so plainly
 * instead of offering a button into a void — **and the draft is still there to copy**, because the
 * member may well have their own way to reach them.
 */
export const CHANNELS_LABEL = 'Send it on';
export const NO_CHANNEL_NOTICE =
  'They have not published a way to reach them, so you will need your own route to them — ' +
  'the draft is still yours to copy.';

// ─── shared ───────────────────────────────────────────────────────────────────────────────────

/** "Founder, Rooted Health", or just one of them, or nothing (`MEM-037` AC-2). */
export function roleAndCompany(person: {
  readonly role: string | null;
  readonly company: string | null;
}): string | null {
  const parts = [person.role, person.company].filter(
    (part): part is string => part !== null && part.trim().length > 0,
  );
  return parts.length === 0 ? null : parts.join(', ');
}

/** The tab badge (`MEM-035` AC-2). A dot, not a number — the prototype's own choice. */
export function hasOutstandingFollowUps(index: { readonly readyTotal: number }): boolean {
  return index.readyTotal > 0;
}

/**
 * **Send**, the opener card's primary action since ADR 052 (`MEM-038` AC-1, `MEM-FUP-008`).
 *
 * The prototype's label is *Send opener*; this is *Send*, because the card's whole subject is the
 * opener above the button and the shorter label is the one that fits beside Copy at 375px. The
 * accessible name names the person, which is what a screen reader needs from a grid of otherwise
 * identical buttons.
 */
export const SEND_OPENER_LABEL = 'Send';
export function sendOpenerAccessibleName(name: string): string {
  return `Send this message to ${name}`;
}

/** What the card says when the server refuses the send. Each is a different fact. */
export const SEND_OPENER_FAILED = 'That message did not send.';
export const SEND_OPENER_DISCONNECTED =
  'You are no longer connected, so this cannot be sent. Copy it and reach them another way.';
export const SEND_OPENER_RATE_LIMITED =
  'You have started a lot of new conversations today. Try the rest tomorrow.';
